import React, { useCallback, useMemo, useRef } from 'react';

import {
  AgentEmptyEntrance,
  AiElementsScope,
  Conversation,
  ConversationContent,
  ConversationScrollButton,
  Loader,
  PromptInput,
  PromptInputAttachButton,
  PromptInputBody,
  PromptInputSpeechRecording,
  PromptInputSpeechStartButton,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
  SPEECH_TO_TEXT_STATUS,
  Suggestion,
  Suggestions,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
  useSpeechToText,
} from '@modernloop/ai-elements';
import { useTranslation } from '@modernloop/i18n';
import { useMountEffect } from '@modernloop/shared/hooks';
import { useTheme } from '@mui/material/styles';

import { Routes } from 'src/constants/routes';

import useHistory from 'src/hooks/useHistory';

import {
  AGENT_SURFACE,
  type AgentChatSession,
  type AgentSurface,
  useAgentChatSession,
} from './AgentChatSessionProvider';
import AgentEmptyGradient from './AgentEmptyGradient';
import AgentGreeting from './AgentGreeting';
import AgentRecentThreads, { selectRecentAgentThreads } from './AgentRecentThreads';
import ThreadSwitcher from './ThreadSwitcher';
import { getAgentThreadRoute } from './agent/agentRoutes';
import { AGENT_CHAT_ITEM } from './agent/chatItems';
import { AGENT_TRANSCRIPT_SEGMENT, toTranscriptSegments } from './agent/transcriptSegments';
import { useAgentFullPageEmptyEntrance } from './agentEmptyEntrance';
import './aiElements.css';
import { muiThemeToAiTokens } from './aiElementsTheme';
import MessageRunPart from './parts/MessageRunPart';
import ToolActivityPart from './parts/ToolActivityPart';
import TurnErrorPart from './parts/TurnErrorPart';

type AgentChatDependencies = {
  history: { push: (route: string) => void };
  session: AgentChatSession;
  translate: (key: string, values?: Record<string, string | number>) => string;
  ThreadSwitcherComponent: React.ComponentType<React.ComponentProps<typeof ThreadSwitcher>>;
};

type Props = {
  focusOnMount?: boolean;
  isFullPage?: boolean;
  surface?: AgentSurface;
  /** Explicit collaborators for isolated rendering tests. Production uses the connected defaults. */
  dependencies?: AgentChatDependencies;
};

type ViewProps = Omit<Props, 'dependencies'> & { dependencies: AgentChatDependencies };

const AGENT_LINK_KIND = {
  EXTERNAL_WEB: 'EXTERNAL_WEB',
  INTERNAL: 'INTERNAL',
  NATIVE: 'NATIVE',
} as const;

/** Parses an Agent response link into the one navigation behavior allowed for that destination. */
export const parseAgentLinkDestination = (url: string, currentOrigin: string) => {
  if (!URL.canParse(url, currentOrigin)) {
    return { kind: AGENT_LINK_KIND.NATIVE } as const;
  }
  const destination = new URL(url, currentOrigin);
  if (destination.protocol !== 'http:' && destination.protocol !== 'https:') {
    return { kind: AGENT_LINK_KIND.NATIVE } as const;
  }
  if (destination.origin !== currentOrigin) {
    return { href: destination.href, kind: AGENT_LINK_KIND.EXTERNAL_WEB } as const;
  }
  return {
    kind: AGENT_LINK_KIND.INTERNAL,
    route: `${destination.pathname}${destination.search}${destination.hash}`,
  } as const;
};

/**
 * Empty-thread prompt suggestions, following the shadcn chatbot-template pattern: a short visible
 * label backed by a fuller prompt that is what actually gets sent.
 */
const AGENT_SUGGESTIONS = [
  {
    labelKey: 'hiring-dashboard-ai-assistant:agent.suggestions.capabilities.label',
    promptKey: 'hiring-dashboard-ai-assistant:agent.suggestions.capabilities.prompt',
  },
  {
    labelKey: 'hiring-dashboard-ai-assistant:agent.suggestions.reschedule.label',
    promptKey: 'hiring-dashboard-ai-assistant:agent.suggestions.reschedule.prompt',
  },
  {
    labelKey: 'hiring-dashboard-ai-assistant:agent.suggestions.availability.label',
    promptKey: 'hiring-dashboard-ai-assistant:agent.suggestions.availability.prompt',
  },
] as const;

const appendVoiceTranscript = (existingValue: string, transcript: string) => {
  const trimmedTranscript = transcript.trim();
  if (!trimmedTranscript) {
    return existingValue;
  }
  const separator = existingValue.length > 0 && !/\s$/.test(existingValue) ? ' ' : '';
  return `${existingValue}${separator}${trimmedTranscript} `;
};

/**
 * One ownership-aware Agent workspace used by the full page, side panel, and dock.
 *
 * The centered empty composer deliberately includes a disabled attachment action: no upload
 * contract exists yet, and a visible explanation is safer than inventing a client-only upload.
 */
export const AgentChatView: React.FC<ViewProps> = ({
  focusOnMount = true,
  isFullPage = false,
  surface = AGENT_SURFACE.SIDE_PANEL,
  dependencies,
}) => {
  const t = dependencies.translate;
  const theme = useTheme();
  const tokens = useMemo(() => muiThemeToAiTokens(theme), [theme]);
  const routerHistory = dependencies.history;
  const ThreadSwitcherComponent = dependencies.ThreadSwitcherComponent;
  const composerTextareaRef = useRef<HTMLTextAreaElement>(null);
  const voiceDraftRef = useRef('');
  const visibleVoiceDraftRef = useRef('');
  const {
    threadId,
    draft,
    setDraft,
    threads,
    items,
    isHistoryLoading,
    isHistoryPending,
    isStreaming,
    sendMessage,
    stopStreaming,
    selectThread,
    newThread,
    registerSurface,
    openThread,
    releaseSurface,
    activeSurface,
  } = dependencies.session;
  const focusComposerTextarea = useCallback(() => {
    requestAnimationFrame(() => composerTextareaRef.current?.focus());
  }, []);
  const speechToText = useSpeechToText({
    onEnd: focusComposerTextarea,
    onFinalTranscript: (transcript) => {
      visibleVoiceDraftRef.current = appendVoiceTranscript(visibleVoiceDraftRef.current, transcript);
      setDraft(visibleVoiceDraftRef.current);
    },
  });
  const isVoiceSession =
    speechToText.status === SPEECH_TO_TEXT_STATUS.ARMING ||
    speechToText.status === SPEECH_TO_TEXT_STATUS.RECORDING ||
    speechToText.status === SPEECH_TO_TEXT_STATUS.FINALIZING;
  const isVoiceWaveformActive =
    speechToText.status === SPEECH_TO_TEXT_STATUS.RECORDING || speechToText.status === SPEECH_TO_TEXT_STATUS.FINALIZING;
  const isVoiceFinalizing = speechToText.status === SPEECH_TO_TEXT_STATUS.FINALIZING;
  const isEmptyThread = items.length === 0 && !isHistoryLoading && !isHistoryPending;
  const { animateEntrance, onEntranceComplete } = useAgentFullPageEmptyEntrance(isFullPage, isEmptyThread);
  const lastSuggestionIndex = AGENT_SUGGESTIONS.length - 1;
  // Recent-conversation links are a full-page-only affordance: the dock and side panel have no
  // room for them, and their history lives in the thread switcher instead.
  const showRecentThreads = isFullPage && isEmptyThread && selectRecentAgentThreads(threads, threadId).length > 0;

  const startVoiceRecording = useCallback(() => {
    voiceDraftRef.current = draft;
    visibleVoiceDraftRef.current = draft;
    speechToText.start();
  }, [draft, speechToText]);
  const cancelVoiceRecording = useCallback(() => {
    setDraft(voiceDraftRef.current);
    speechToText.cancel();
    // Restore focus after the recording toolbar closes so typing can continue immediately.
    focusComposerTextarea();
  }, [focusComposerTextarea, setDraft, speechToText]);
  const completeVoiceRecording = useCallback(() => {
    speechToText.finish(2000);
  }, [speechToText]);

  useMountEffect(() => {
    registerSurface(surface);
    openThread(surface);
    if (focusOnMount) {
      focusComposerTextarea();
    }
    return () => releaseSurface(surface);
  });

  const handleLinkClick = useCallback(
    (url: string, event: React.MouseEvent<HTMLAnchorElement>) => {
      const destination = parseAgentLinkDestination(url, window.location.origin);
      if (destination.kind === AGENT_LINK_KIND.NATIVE) {
        return;
      }
      event.preventDefault();
      if (destination.kind === AGENT_LINK_KIND.EXTERNAL_WEB) {
        window.open(destination.href, '_blank', 'noopener,noreferrer');
        return;
      }
      routerHistory.push(destination.route);
    },
    [routerHistory]
  );
  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isStreaming || isHistoryPending || draft.trim().length === 0) {
        return;
      }
      if (isFullPage && isEmptyThread) {
        // The first turn gives a new conversation its durable, shareable browser location.
        routerHistory.push(getAgentThreadRoute(threadId));
      }
      void sendMessage(draft, surface);
      setDraft('');
    },
    [
      draft,
      isEmptyThread,
      isFullPage,
      isHistoryPending,
      isStreaming,
      routerHistory,
      sendMessage,
      setDraft,
      surface,
      threadId,
    ]
  );
  const handleSelectThread = useCallback(
    (nextThreadId: string) => {
      selectThread(nextThreadId, surface);
      routerHistory.push(getAgentThreadRoute(nextThreadId));
      focusComposerTextarea();
    },
    [focusComposerTextarea, routerHistory, selectThread, surface]
  );
  const handleSuggestionClick = useCallback(
    (prompt: string) => {
      if (isStreaming || isHistoryPending) {
        return;
      }
      if (isFullPage && isEmptyThread) {
        routerHistory.push(getAgentThreadRoute(threadId));
      }
      void sendMessage(prompt, surface);
      // Clear any typed draft like composer submit does, so it cannot be sent as an accidental
      // follow-up after the suggestion (Bugbot on this PR).
      setDraft('');
    },
    [isEmptyThread, isFullPage, isHistoryPending, isStreaming, routerHistory, sendMessage, setDraft, surface, threadId]
  );
  const lastItem = items.at(-1);
  const showPendingBubble =
    isStreaming && !(lastItem?.type === AGENT_CHAT_ITEM.ASSISTANT_MESSAGE && lastItem.isStreaming);
  const segments = useMemo(() => toTranscriptSegments(items, showPendingBubble), [items, showPendingBubble]);
  const showEmptyPresentation = isFullPage || surface === AGENT_SURFACE.DOCK;
  const canvasClassName = isFullPage ? 'mx-auto w-full max-w-[960px] px-2 sm:px-4' : 'w-full';
  // The dock header floats over the transcript as a translucent blur layer, so the transcript
  // scrolls under it but starts its first message below the header's footprint.
  const transcriptClassName = surface === AGENT_SURFACE.DOCK ? cn(canvasClassName, 'pt-12') : canvasClassName;
  // The full-page header is not part of the centered reading column: it keeps the canvas gutter
  // but spans the surface, so the title and its history menu sit at the left edge.
  const headerClassName = isFullPage ? 'w-full px-2 pt-3 sm:px-4' : 'w-full';

  if (activeSurface !== surface) {
    return null;
  }

  const composer = (
    <PromptInput onSubmit={handleSubmit}>
      {/* The composer sits on the Lotus gradient, not a flat page, so it takes the raised surface
          to stay legible against it. */}
      <PromptInputBody variant="raised">
        <PromptInputTextarea
          aria-label={t('hiring-dashboard-ai-assistant:agent.inputPlaceholder')}
          disabled={isHistoryPending || isVoiceSession}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('hiring-dashboard-ai-assistant:agent.inputPlaceholder')}
          ref={composerTextareaRef}
          value={draft}
        />
        {isVoiceSession ? (
          <PromptInputToolbar className="w-full">
            <PromptInputSpeechRecording
              active={isVoiceWaveformActive}
              cancelLabel={t('hiring-dashboard-ai-assistant:agent.composer.voiceInput.cancel')}
              completeLabel={t('hiring-dashboard-ai-assistant:agent.composer.voiceInput.complete')}
              onCancel={cancelVoiceRecording}
              onComplete={completeVoiceRecording}
              processing={isVoiceFinalizing}
            />
          </PromptInputToolbar>
        ) : (
          <PromptInputToolbar className="justify-between">
            <PromptInputTools>
              <TooltipProvider>
                <Tooltip>
                  {/* A disabled control cannot fire the pointer events Radix listens for, so the
                    trigger wraps it and carries the hover target itself. */}
                  <TooltipTrigger asChild>
                    <span>
                      <PromptInputAttachButton
                        aria-describedby="agent-attachment-unavailable"
                        disabled
                        label={t('hiring-dashboard-ai-assistant:agent.composer.attach')}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{t('hiring-dashboard-ai-assistant:agent.composer.attachUnavailable')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="sr-only" id="agent-attachment-unavailable">
                {t('hiring-dashboard-ai-assistant:agent.composer.attachUnavailable')}
              </span>
              <PromptInputSpeechStartButton
                disabled={isHistoryPending}
                label={t(
                  speechToText.status === SPEECH_TO_TEXT_STATUS.ERROR
                    ? 'hiring-dashboard-ai-assistant:agent.composer.voiceInput.error'
                    : 'hiring-dashboard-ai-assistant:agent.composer.voiceInput.start'
                )}
                onClick={startVoiceRecording}
                supported={speechToText.isSupported}
                unsupportedLabel={t('hiring-dashboard-ai-assistant:agent.composer.voiceInput.unsupported')}
              />
            </PromptInputTools>
            <PromptInputSubmit
              aria-label={
                isStreaming
                  ? t('hiring-dashboard-ai-assistant:agent.stop')
                  : t('hiring-dashboard-ai-assistant:agent.send')
              }
              data-testid="agent-stop-button"
              disabled={!isStreaming && isHistoryPending}
              onClick={isStreaming ? stopStreaming : undefined}
              status={isStreaming ? 'streaming' : 'ready'}
            />
          </PromptInputToolbar>
        )}
      </PromptInputBody>
    </PromptInput>
  );

  return (
    // `isolate` keeps the gradient's negative layer inside the chat: it may sit behind the header
    // and the transcript, but never behind the surface the chat is painted on.
    <AiElementsScope className="relative isolate flex h-full min-h-0 w-full flex-col" tokens={tokens}>
      <AgentEmptyEntrance active={animateEntrance} onComplete={onEntranceComplete}>
        {showEmptyPresentation && <AgentEmptyGradient isEmpty={isEmptyThread} />}
        {/* The header spans the surface and stays transparent and borderless, so the gradient runs
            behind it as one uninterrupted canvas. */}
        {isFullPage && (
          <div className="w-full" data-testid="agent-chat-header">
            <div className={headerClassName}>
              <ThreadSwitcherComponent
                activeThreadId={threadId}
                isEmptyThread={isEmptyThread}
                onNewThread={() => {
                  newThread(surface);
                  routerHistory.push(Routes.Agent);
                  focusComposerTextarea();
                }}
                onSelectThread={handleSelectThread}
                threads={threads}
              />
            </div>
          </div>
        )}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <Conversation
            aria-busy={isHistoryPending || isStreaming}
            aria-label={t('hiring-dashboard-ai-assistant:agent.page.conversationLabel')}
            aria-live="polite"
            className="min-h-0"
            role="log"
            tabIndex={0}
          >
            {isEmptyThread ? (
              <ConversationContent className="h-full p-0">
                <div className="h-full" data-testid="agent-empty-canvas" />
              </ConversationContent>
            ) : (
              <ConversationContent className={transcriptClassName}>
                {isHistoryLoading && (
                  <div
                    aria-label={t('hiring-dashboard-ai-assistant:agent.page.loading')}
                    className="flex items-center justify-center p-4"
                    role="status"
                  >
                    <Loader label={t('hiring-dashboard-ai-assistant:agent.page.loading')} size={20} />
                  </div>
                )}
                {segments.map((segment) => {
                  switch (segment.type) {
                    case AGENT_TRANSCRIPT_SEGMENT.MESSAGE_RUN:
                      return <MessageRunPart key={segment.key} onLinkClick={handleLinkClick} segment={segment} />;
                    case AGENT_TRANSCRIPT_SEGMENT.TOOL_ACTIVITY:
                      return <ToolActivityPart key={segment.key} segment={segment} />;
                    case AGENT_TRANSCRIPT_SEGMENT.TURN_ERROR:
                      return <TurnErrorPart key={segment.key} segment={segment} />;
                    default:
                      return null;
                  }
                })}
              </ConversationContent>
            )}
            <ConversationScrollButton />
          </Conversation>
          <div
            className={cn(
              canvasClassName,
              // The centered empty composer deliberately narrows and pads past the canvas defaults,
              // so it is merged last: plain concatenation would leave both widths live and let
              // stylesheet order, not intent, pick the winner. `sm:p-6` is explicit because a
              // responsive `sm:px-4` is a separate merge group that a bare `p-6` cannot displace.
              isEmptyThread
                ? 'absolute top-1/2 left-1/2 w-full max-w-[640px] -translate-x-1/2 -translate-y-1/2 p-6 sm:p-6'
                : 'p-2'
            )}
            data-testid="agent-composer-region"
          >
            {/* The side panel stays compact and plain; the full page and dock share the intentional
                new-conversation greeting and gradient. */}
            {isEmptyThread && showEmptyPresentation && (
              <AgentEmptyEntrance.Greeting>
                <AgentGreeting />
              </AgentEmptyEntrance.Greeting>
            )}
            <AgentEmptyEntrance.Composer>{composer}</AgentEmptyEntrance.Composer>
            {isEmptyThread && (
              <div className="mt-3">
                <Suggestions data-testid="agent-suggestions">
                  {AGENT_SUGGESTIONS.map((suggestion, index) => (
                    <AgentEmptyEntrance.Suggestion
                      index={index}
                      isLast={index === lastSuggestionIndex && !showRecentThreads}
                      key={suggestion.labelKey}
                    >
                      <Suggestion onSuggestionClick={handleSuggestionClick} suggestion={t(suggestion.promptKey)}>
                        {t(suggestion.labelKey)}
                      </Suggestion>
                    </AgentEmptyEntrance.Suggestion>
                  ))}
                </Suggestions>
              </div>
            )}
            {showRecentThreads && (
              // The links join the entrance as one final staggered row after the suggestion chips,
              // so they own the sequence-complete callback the last chip would otherwise fire.
              <AgentEmptyEntrance.Suggestion className="mt-8" index={AGENT_SUGGESTIONS.length} isLast>
                <AgentRecentThreads
                  activeThreadId={threadId}
                  onSelectThread={handleSelectThread}
                  threads={threads}
                  translate={t}
                />
              </AgentEmptyEntrance.Suggestion>
            )}
          </div>
        </div>
      </AgentEmptyEntrance>
    </AiElementsScope>
  );
};

const AgentChat: React.FC<Props> = ({ dependencies, ...props }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const session = useAgentChatSession();
  return (
    <AgentChatView
      {...props}
      dependencies={
        dependencies ?? {
          history,
          session,
          translate: (key, values) => t(key, values),
          ThreadSwitcherComponent: ThreadSwitcher,
        }
      }
    />
  );
};

export default AgentChat;
